import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { DocumentDescriptionDTO } from '@iota/iota-management-portal-v4-client';
import { ModalService } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import { DownloadDocument, LoadDocument } from 'src/app/store/actions/document.action';
import { DocumentDescriptionWithUrl, documentsByDocumentDescriptionIds } from 'src/app/store/reducer/document.reducer';
import { DocumentLabelDescriptions } from '../../constants-and-enums/document-labels';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../../dialogs/dialog-confirmation/dialog-confirmation.component';
import { DialogShowDocumentComponent, DialogShowDocumentData } from '../../dialogs/dialog-show-document/dialog-show-document.component';
import { LabelSelectionDialogComponent } from './label-selection-dialog/label-selection-dialog.component';

@Component({
    standalone: false,
    selector: 'app-document-list',
    templateUrl: './document-list.component.html',
    styleUrls: ['./document-list.component.scss']
})
export class DocumentListComponent implements OnInit, OnChanges {
    @Input() attachments: DocumentDescriptionDTO[] = [] as DocumentDescriptionDTO[];
    @Input() header = $localize`Documents`;
    @Input() labelSelection: string[];

    @Output() remove: EventEmitter<DocumentDescriptionDTO> = new EventEmitter();
    @Output() upload: EventEmitter<File> = new EventEmitter();
    @Output() labelsSelected: EventEmitter<string[]> = new EventEmitter();

    selectedFiles: FileList;
    currentFileUpload: File;

    documents$: Observable<DocumentDescriptionWithUrl[]>;

    constructor(
        private dialog: ModalService,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.loadDocuments();
    }

    ngOnChanges() {
        this.loadDocuments();
    }

    loadDocuments(): void {
        const ids = this.attachments?.map((d) => d.documentDescriptionId) ?? [];

        this.documents$ = this.store.select(documentsByDocumentDescriptionIds(ids));

        this.store
            .select(documentsByDocumentDescriptionIds(ids))
            .pipe(take(1))
            .subscribe((docs) => {
                const loadedIds = docs?.map((d) => d.documentDescriptionId) ?? [];

                const notLoaded = this.attachments?.filter((d) => !loadedIds.includes(d.documentDescriptionId)) ?? [];
                notLoaded?.forEach((d) => this.store.dispatch(LoadDocument({ document: d })));
            });
    }

    isImage(document: DocumentDescriptionDTO): boolean {
        return document.contentType && document.contentType.toLowerCase().startsWith('image');
    }

    openInput() {
        if (this.labelSelection) {
            const dialogRef = this.dialog.open(LabelSelectionDialogComponent, {
                data: this.labelSelection
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    this.labelsSelected.emit(result);
                    document.getElementById('fileInput').click();
                }
            });
        } else {
            document.getElementById('fileInput').click();
        }
    }

    selectFile(evt: any) {
        this.selectedFiles = evt.target.files;
        this.uploadFile();
    }

    uploadFile() {
        this.currentFileUpload = this.selectedFiles.item(0);
        this.upload.emit(this.currentFileUpload);
    }

    download(attachment: DocumentDescriptionDTO) {
        this.store.dispatch(DownloadDocument({ document: attachment }));
    }

    showDocument(document: DocumentDescriptionDTO) {
        const data: DialogShowDocumentData = {
            document
        };
        this.dialog.open(DialogShowDocumentComponent, {
            data
        });
    }

    getFileIconPath(document: DocumentDescriptionDTO): string {
        return 'file/' + document?.name.split('.').slice(-1).join('').toLowerCase() + '.svg';
    }

    removeAttachment(attachment: DocumentDescriptionDTO) {
        if (!attachment) {
            return;
        }

        const data: ConfirmationDialogData = {
            title: $localize`Delete document`,
            text: $localize`Are you sure you want to delete the document?`,
            confirmButtonStyle: 'error'
        };

        this.dialog
            .open(DialogConfirmationComponent, {
                data
            })
            .afterClosed()
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.remove.emit(attachment);
                }
            });
    }

    getShowableLabels(attachment: DocumentDescriptionDTO): string[] {
        return attachment.labels?.filter((l) => this.labelSelection.includes(l));
    }

    getLabelDescription(label: string) {
        return DocumentLabelDescriptions.get(label);
    }
}
